// components/Fretboard.tsx
import { useState, useEffect, useRef, useMemo, useCallback, type MouseEvent, memo } from 'react'

import { 
  NUM_STRINGS, 
  NUM_FRETS_DISPLAY, 
  NOTE_COLORS,
  SCALE_DEGREE_DISPLAY_MAP,
  NOTES,
  type ScaleDegree
} from '../utils/constants'
import { calculateNotesToDisplay, calculateCagedShape, calculateScaleIntersection, calculateScale, inferChordQuality } from '../utils/musicTheory'
import { playNote, setSoundType, type SoundType } from '../utils/audio'
import { getFrequencyForPosition } from '../utils/noteFrequencies'
import { 
  type GuitarArrangement, 
  type Note
} from '../presets/presetSchema'

// CAGED shape enhancement constants
const CAGED_ENHANCEMENTS = {
  // When CAGED shapes are active - notes IN CAGED shapes
  cagedFillRadiusMultiplier: 1.0,
  cagedBorderRadiusMultiplier: 1.05,
  cagedOpacity: 0.5,
  cagedBorderWidth: 5,
  cagedDimmingMultiplier: 1.2,
  cagedStringOpacity: 0.02,
  
  // When CAGED shapes are active - notes NOT in CAGED shapes (shrunk)
  nonCagedFillRadiusMultiplier: 0.8,
  nonCagedBorderRadiusMultiplier: 0.85,
  nonCagedOpacity: 0.4,
  nonCagedBorderWidth: 2,
  nonCagedDimmingMultiplier: 1.0,
  nonCagedStringOpacity: 0.08,
  
  // When NO CAGED shapes are active - normal state
  normalFillRadiusMultiplier: 0.9,
  normalBorderRadiusMultiplier: 0.95,
  normalOpacity: 0.5,
  normalBorderWidth: 3,
  normalDimmingMultiplier: 1.2,
  normalStringOpacity: 0.05
};

// Overlay visualization effect constants
const OVERLAY_ENHANCEMENTS = {
  // GLOW effect - enhanced highlighting for intersection notes
  glowOuterRadius: 1.6,
  glowInnerRadius: 1.2,
  glowOpacity: 0.4,
  glowPulse: 0.15,
  
  // GROW effect - size scaling for intersection notes
  growFillRadiusMultiplier: 1.05, // Make intersection notes larger
  growBorderRadiusMultiplier: 1.10, // Make intersection notes larger
  growBorderWidth: 5,
  
  // GROW effect - shrink other notes to emphasize intersection notes
  shrinkFillRadiusMultiplier: 0.8, // Shrink non-intersection notes
  shrinkBorderRadiusMultiplier: 0.85, // Shrink non-intersection notes
  shrinkOpacity: 0.4, // Dim non-intersection notes
  
  // PULSE effect - breathing animation for intersection notes
  pulseSizeVariation: 0.25,
  pulseOpacityVariation: 0.3,
  pulseFrequency: 0.6,
  
  // Colors for overlay intersection notes
  intersectionBg: '#FFD700',
  intersectionBorder: '#FF6B35',
  intersectionText: '#1A1A1A',
  intersectionGlow: '#FFD700'
};

// Component props interface
interface FretboardProps {
  arrangement: GuitarArrangement;
  soundType: SoundType;
}

// Note display types
interface NoteDisplay {
  note: Note;
  type: string;
  string: number;
  fret: number;
  overlayType: 'primary-only' | 'overlay-only' | 'intersection';
}

// CAGED note interface
interface CagedNote {
  string: number;
  fret: number;
  shapeKey: string;
  patternId: number;
}

// Note position interface for string dimming
interface NotePosition {
  start: number;
  end: number;
  isInCagedShape: boolean;
  stringOpacity: number;
}

// Connection line interface
interface ConnectionLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  shapeKey: string;
}

// Color info interface
interface ColorInfo {
  bg: string;
  border: string;
  text: string;
  opacity?: number;
}

export function Fretboard({ arrangement, soundType }: FretboardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 200 });
  const [notes, setNotes] = useState<NoteDisplay[]>([]);
  const [cagedNotes, setCagedNotes] = useState<CagedNote[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const animationStartTimeRef = useRef<number | null>(null);

  // Memoize expensive fretboard note calculations
  const notesToDisplay = useMemo(() => {
    return calculateNotesToDisplay(arrangement.root.note, arrangement.root.scale);
  }, [arrangement.root.note, arrangement.root.scale]);
  
  // Memoize overlay intersection calculation
  const overlayIntersection = useMemo(() => {
    if (!arrangement.overlay || 
        !arrangement.overlay.note || 
        !arrangement.overlay.quality ||
        !NOTES.includes(arrangement.overlay.note)) {
      return null;
    }
    
    try {
      // Check for same scale edge case
      const isSameScale = arrangement.root.note === arrangement.overlay.note && 
                         arrangement.root.scale === arrangement.overlay.quality;
      
      if (isSameScale) {
        // If both scales are identical, treat all primary notes as intersections
        const primaryScale = calculateScale(arrangement.root.note, arrangement.root.scale);
        const noteClassifications: Record<number, string> = {};
        
        // Mark all primary scale notes as intersections
        for (let noteIndex = 0; noteIndex < 12; noteIndex++) {
          const isInPrimary = primaryScale.noteIndices.includes(noteIndex);
          noteClassifications[noteIndex] = isInPrimary ? 'intersection' : 'neither';
        }
        
        return {
          noteClassifications,
          intersectionNotes: primaryScale.noteIndices
        };
      } else {
        return calculateScaleIntersection(
          arrangement.root.note, 
          arrangement.root.scale,
          arrangement.overlay.note, 
          arrangement.overlay.quality
        );
      }
    } catch {
      // Error calculating overlay intersection
      return null;
    }
  }, [
    arrangement.root.note, 
    arrangement.root.scale, 
    arrangement.overlay
  ]);
  
  // Memoize notes with overlay classification
  const notesWithOverlay = useMemo(() => {
    return notesToDisplay.map(note => {
      const noteIndex = NOTES.indexOf(note.note);
      let overlayType: 'primary-only' | 'overlay-only' | 'intersection' = 'primary-only'; // default for notes in primary scale
      
      if (overlayIntersection && noteIndex !== -1) {
        overlayType = (overlayIntersection.noteClassifications[noteIndex] as 'primary-only' | 'overlay-only' | 'intersection') || 'primary-only';
      }
      
      return {
        ...note,
        overlayType
      };
    });
  }, [notesToDisplay, overlayIntersection]);
  
  // Memoize chord quality calculation
  const chordQuality = useMemo(() => {
    return arrangement.caged?.quality || inferChordQuality(arrangement.root.scale);
  }, [arrangement.caged?.quality, arrangement.root.scale]);
  
  // Memoize CAGED shape calculations
  const cagedShapeNotes = useMemo(() => {
    const cagedNotes: CagedNote[] = [];
    
    if (arrangement.caged?.activeShapes) {
      for (const [shapeKey, isActive] of Object.entries(arrangement.caged.activeShapes)) {
        if (!isActive) continue;
        
        const basePattern = calculateCagedShape(
          arrangement.root.note, 
          shapeKey as keyof typeof arrangement.caged.activeShapes, 
          chordQuality
        );
        cagedNotes.push(...basePattern.map(note => ({ ...note, shapeKey, patternId: 0 })));
        
        // Add octave repeat if within display range
        const repeatPattern = basePattern.map(note => ({ ...note, fret: note.fret + 12 }));
        if (repeatPattern.every(note => note.fret <= NUM_FRETS_DISPLAY)) {
          cagedNotes.push(...repeatPattern.map(note => ({ ...note, shapeKey, patternId: 1 })));
        }
      }
    }
    
    return cagedNotes;
  }, [arrangement, chordQuality]);
  
  // Update state when memoized values change
  useEffect(() => {
    setNotes(notesWithOverlay);
    setCagedNotes(cagedShapeNotes);
  }, [notesWithOverlay, cagedShapeNotes]);

  // Memoize resize handler for performance
  const handleResize = useCallback(() => {
    const container = canvasRef.current?.parentElement;
    if (container) {
      setDimensions({
        width: container.clientWidth - 20, // Account for container padding
        height: container.clientHeight - 20 // Use full container height minus padding
      });
    }
  }, []);

  // Handle window resize
  useEffect(() => {
    handleResize(); // Initial sizing
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Animation loop for pulse effect - run when overlay pulse is active
  useEffect(() => {
    const isOverlayPulseActive = arrangement.overlay?.visualizations?.PULSE && 
                                arrangement.overlay?.note && 
                                arrangement.overlay?.quality;
    const shouldAnimate = isOverlayPulseActive;
    
    if (shouldAnimate) {
      const animate = (currentTime: number) => {
        if (!animationStartTimeRef.current) {
          animationStartTimeRef.current = currentTime;
        }
        
        // Force re-render by updating dimensions slightly
        setDimensions(prev => ({ ...prev }));
        
        animationFrameRef.current = requestAnimationFrame(animate);
      }
      
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      animationStartTimeRef.current = null;
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [
    arrangement.overlay?.visualizations?.PULSE, 
    arrangement.overlay?.note,
    arrangement.overlay?.quality
  ]);

  // Draw the fretboard
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const dpr = window.devicePixelRatio || 1; // Use actual device pixel ratio instead of fixed value
    
    // Calculate layout parameters here to avoid dependency issues
    // Add vertical padding (20px) to prevent strings from being at the very edge
    const verticalPadding = 20;
    const fretSpacing = dimensions.width / (NUM_FRETS_DISPLAY + 1);
    const stringSpacing = (dimensions.height - (verticalPadding * 2)) / (NUM_STRINGS - 1); 
    const nutWidth = 8;
    const noteRadius = Math.min(fretSpacing, stringSpacing) * 0.4;
    const nutOffset = fretSpacing / 2 - nutWidth / 2;
    
    // Helper function to get all note positions for a string
    const getNotePositionsForString = (stringIndex: number): NotePosition[] => {
      const allNotes = [...notes, ...cagedNotes];
      
      return allNotes
        .filter(note => note.string === stringIndex)
        .map(note => {
          // Check if this note is part of any active CAGED shape
          const isInCagedShape = cagedNotes.some(cagedNote => 
            cagedNote.string === note.string && 
            cagedNote.fret === note.fret &&
            arrangement.caged?.activeShapes?.[cagedNote.shapeKey as keyof typeof arrangement.caged.activeShapes]
          );
          
          const noteX = note.fret === 0
            ? fretSpacing * 0.25
            : (note.fret - 0.5) * fretSpacing + nutOffset;
          
          // Use normal note radius and opacity (LINK is always enabled)
          const noteVisualRadius = noteRadius * CAGED_ENHANCEMENTS.normalBorderRadiusMultiplier;
          const stringOpacity = CAGED_ENHANCEMENTS.normalStringOpacity;
          
          return {
            start: noteX - noteVisualRadius,
            end: noteX + noteVisualRadius,
            isInCagedShape,
            stringOpacity
          };
        })
        .sort((a, b) => a.start - b.start);
    };
    
    // Set canvas size accounting for device pixel ratio
    canvas.width = dimensions.width * dpr;
    canvas.height = dimensions.height * dpr;
    canvas.style.width = `${dimensions.width}px`;
    canvas.style.height = `${dimensions.height}px`;
    
    ctx.scale(dpr, dpr);
    
    // Clear canvas
    ctx.clearRect(0, 0, dimensions.width, dimensions.height);
    
    // Add subtle grid lines in the background
    ctx.strokeStyle = 'rgba(0, 50, 80, 0.15)';
    ctx.lineWidth = 0.5;
    
    // Draw vertical grid lines
    const gridSize = 25;
    for (let i = 0; i <= dimensions.width; i += gridSize) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, dimensions.height);
      ctx.stroke();
    }
    
    // Draw horizontal grid lines
    for (let i = 0; i <= dimensions.height; i += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(dimensions.width, i);
      ctx.stroke();
    }
    
    // Draw strings with dimming where notes are present
    ctx.lineWidth = 1.5;
    // Reset any previous shadow effects
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    
    for (let stringIndex = 0; stringIndex < NUM_STRINGS; stringIndex++) {
      const y = verticalPadding + stringIndex * stringSpacing;
      const notePositions = getNotePositionsForString(stringIndex);
      
      let currentX = 0;
      
      for (const notePos of notePositions) {
        // Draw normal string segment before the note
        if (currentX < notePos.start) {
          ctx.strokeStyle = '#00DDFF';
          ctx.beginPath();
          ctx.moveTo(Math.max(0, currentX), y);
          ctx.lineTo(Math.min(dimensions.width, notePos.start), y);
          ctx.stroke();
        }
        
        // Draw dimmed string segment under the note
        // Use dynamic opacity based on note state
        ctx.strokeStyle = `rgba(0, 221, 255, ${notePos.stringOpacity})`;
        ctx.beginPath();
        ctx.moveTo(Math.max(0, notePos.start), y);
        ctx.lineTo(Math.min(dimensions.width, notePos.end), y);
        ctx.stroke();
        
        currentX = Math.max(currentX, notePos.end);
      }
      
      // Draw remaining normal string segment after all notes
      if (currentX < dimensions.width) {
        ctx.strokeStyle = '#00DDFF';
        ctx.beginPath();
        ctx.moveTo(currentX, y);
        ctx.lineTo(dimensions.width, y);
        ctx.stroke();
      }
    }
    
    // Draw nut
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = nutWidth;
    
    ctx.beginPath();
    ctx.moveTo(nutOffset, verticalPadding - stringSpacing * 0.1);
    ctx.lineTo(nutOffset, dimensions.height - verticalPadding + stringSpacing * 0.1);
    ctx.stroke();
    
    // Draw frets
    ctx.strokeStyle = '#00DDFF';
    ctx.lineWidth = 1.5;
    
    for (let i = 1; i <= NUM_FRETS_DISPLAY; i++) {
      const x = i * fretSpacing + nutOffset;
      ctx.beginPath();
      ctx.moveTo(x, verticalPadding - stringSpacing * 0.1);
      ctx.lineTo(x, dimensions.height - verticalPadding + stringSpacing * 0.1);
      ctx.stroke();
    }
    
    // Draw fret markers
    ctx.fillStyle = '#00DDFF66';
    const markers: Record<number, number[]> = { 3: [2.5], 5: [2.5], 7: [2.5], 9: [2.5], 12: [1.5, 3.5], 15: [2.5] };
    for (const [fretNum, positions] of Object.entries(markers)) {
      const fret = Number.parseInt(fretNum, 10);
      for (const pos of positions) {
        const x = (fret - 0.5) * fretSpacing + nutOffset;
        ctx.beginPath();
        ctx.arc(x, verticalPadding + pos * stringSpacing, stringSpacing * 0.2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // Define colors for CAGED shapes
    const cagedShapeColors: Record<string, string> = {
      'C': '#FF355E', // Neon red
      'A': '#FF9E00', // Neon orange
      'G': '#39FF14', // Neon green
      'E': '#00DDFF', // Neon blue
      'D': '#9D4EDD'  // Neon purple
    };
      
    // Helper function to get note radius (simplified since CAGED GROW removed)
    const getNoteRadius = (): number => {
      // Always use normal border radius since CAGED GROW is removed
      return noteRadius * CAGED_ENHANCEMENTS.normalBorderRadiusMultiplier;
    };

    // Helper function to blend two colors
    const blendColors = (color1: string, color2: string): string => {
      const hex1 = color1.replace('#', '');
      const hex2 = color2.replace('#', '');
      
      const r1 = Number.parseInt(hex1.substring(0, 2), 16);
      const g1 = Number.parseInt(hex1.substring(2, 4), 16);
      const b1 = Number.parseInt(hex1.substring(4, 6), 16);
      
      const r2 = Number.parseInt(hex2.substring(0, 2), 16);
      const g2 = Number.parseInt(hex2.substring(2, 4), 16);
      const b2 = Number.parseInt(hex2.substring(4, 6), 16);
      
      const r = Math.round((r1 + r2) / 2);
      const g = Math.round((g1 + g2) / 2);
      const b = Math.round((b1 + b2) / 2);
      
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    // Helper function to check if two line segments overlap
    const doLinesOverlap = (x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, x4: number, y4: number, threshold = 5): boolean => {
      // Calculate distance between each point of line 1 to line 2
      const distancesToLine2: number[] = [];
      const segments = 20;
      
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const px = x1 + t * (x2 - x1);
        const py = y1 + t * (y2 - y1);
        
        // Distance from point to line segment
        const A = px - x3;
        const B = py - y3;
        const C = x4 - x3;
        const D = y4 - y3;
        
        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;
        if (lenSq !== 0) param = dot / lenSq;
        
        let xx: number, yy: number;
        if (param < 0) {
          xx = x3;
          yy = y3;
        } else if (param > 1) {
          xx = x4;
          yy = y4;
        } else {
          xx = x3 + param * C;
          yy = y3 + param * D;
        }
        
        const dx = px - xx;
        const dy = py - yy;
        const distance = Math.sqrt(dx * dx + dy * dy);
        distancesToLine2.push(distance);
      }
      
      return distancesToLine2.some(d => d <= threshold);
    };

    // Helper function to draw a line segment with dimming based on note proximity
    const drawConnectionSegment = (x1: number, y1: number, x2: number, y2: number, color: string, segments = 200, otherLines: ConnectionLine[] = []) => {
      ctx.lineWidth = 8;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Fade control parameters (tune these values for faster/sooner fades)
      const FADE_START = 1;    // normalizedDistance below this = fully invisible
      const FADE_END = 2;      // normalizedDistance above this = full opacity
      const MAX_OPACITY = 0.9;   // maximum line opacity
      const FADE_RATE = .9;       // exponential fade rate (higher = sharper)
      const dx = (x2 - x1) / segments;
      const dy = (y2 - y1) / segments;
      
      // Calculate opacity for each point along the line
      const opacities: number[] = [];
      for (let i = 0; i <= segments; i++) {
        const x = x1 + dx * i;
        const y = y1 + dy * i;
        
        // Find the closest note to determine dynamic radius for opacity zones
        const allNotes = [...notes, ...cagedNotes];
        let closestNoteRadius = noteRadius * CAGED_ENHANCEMENTS.normalBorderRadiusMultiplier;
        let minDistance = Infinity;
        
        for (const note of allNotes) {
          const noteX = note.fret === 0
            ? fretSpacing * 0.25
            : (note.fret - 0.5) * fretSpacing + nutOffset;
          const noteY = verticalPadding + note.string * stringSpacing;
          const distance = Math.sqrt((x - noteX) ** 2 + (y - noteY) ** 2);
          
          if (distance < minDistance) {
            minDistance = distance;
            closestNoteRadius = getNoteRadius();
          }
        }
        
        // Calculate opacity based on proximity using exponential fade controlled by tuneable parameters
        const normalizedDistance = minDistance / closestNoteRadius;
        let opacity: number;
        if (normalizedDistance <= FADE_START) {
          opacity = 0;
        } else if (normalizedDistance < FADE_END) {
          const t = (normalizedDistance - FADE_START) / (FADE_END - FADE_START);
          opacity = MAX_OPACITY * (1 - Math.exp(-FADE_RATE * t));
        } else {
          opacity = MAX_OPACITY;
        }
        
        opacities.push(opacity);
      }
      
      // Draw the line using multiple segments with smooth opacity transitions and segment-by-segment color blending
      for (let i = 0; i < segments; i++) {
        const startX = x1 + dx * i;
        const startY = y1 + dy * i;
        const endX = x1 + dx * (i + 1);
        const endY = y1 + dy * (i + 1);
        
        // Check if this specific segment overlaps with any other line segments
        let segmentColor = color;
        for (const otherLine of otherLines) {
          if (doLinesOverlap(startX, startY, endX, endY, otherLine.x1, otherLine.y1, otherLine.x2, otherLine.y2, 4)) {
            segmentColor = blendColors(segmentColor, otherLine.color);
          }
        }
        
        // Use the average opacity of the start and end points for smoother transitions
        const avgOpacity = (opacities[i] + opacities[i + 1]) / 2;
        
        const hex = segmentColor.replace('#', '');
        const r = Number.parseInt(hex.substring(0, 2), 16);
        const g = Number.parseInt(hex.substring(2, 4), 16);
        const b = Number.parseInt(hex.substring(4, 6), 16);
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${avgOpacity})`;
        
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
      }
    };

    // Draw CAGED shapes with adaptive dimming under notes
    if (cagedNotes.length > 0 && arrangement.caged?.visualizations?.LINK) {
      // Collect all connection lines first
      const allLines: ConnectionLine[] = [];
      
      // Group notes by shapeKey then by patternId
      const grouped: Record<string, Record<number, CagedNote[]>> = {};
      for (const note of cagedNotes) {
        if (!arrangement.caged?.activeShapes?.[note.shapeKey as keyof typeof arrangement.caged.activeShapes]) continue;
        grouped[note.shapeKey] = grouped[note.shapeKey] || {};
        const pid = note.patternId || 0;
        grouped[note.shapeKey][pid] = grouped[note.shapeKey][pid] || [];
        grouped[note.shapeKey][pid].push(note);
      }
      
      // Collect all lines
      Object.entries(grouped).forEach(([shapeKey, patterns]) => {
        Object.values(patterns).forEach(pts => {
          if (pts.length < 2) return;
          const sortedPts = pts.sort((a, b) => b.string - a.string);
          const color = cagedShapeColors[shapeKey] || cagedShapeColors['E'];
          
          // Collect connection lines between consecutive points
          for (let i = 0; i < sortedPts.length - 1; i++) {
            const point1 = sortedPts[i];
            const point2 = sortedPts[i + 1];
            
            const x1 = point1.fret === 0
              ? fretSpacing * 0.25
              : (point1.fret - 0.5) * fretSpacing + nutOffset;
            const y1 = verticalPadding + point1.string * stringSpacing;
            const x2 = point2.fret === 0
              ? fretSpacing * 0.25
              : (point2.fret - 0.5) * fretSpacing + nutOffset;
            const y2 = verticalPadding + point2.string * stringSpacing;
            
            allLines.push({ x1, y1, x2, y2, color, shapeKey });
          }
        });
      });
      
      // Draw each line with knowledge of all other lines
      allLines.forEach(line => {
        const otherLines = allLines.filter(other => 
          other !== line && other.shapeKey !== line.shapeKey
        );
        drawConnectionSegment(line.x1, line.y1, line.x2, line.y2, line.color, 200, otherLines);
      });
    }
    
    // Draw scale notes with neon glow
    for (const note of notes) {
      const x = (note.fret === 0 ? 
        fretSpacing * 0.25 : 
        (note.fret - 0.5) * fretSpacing + nutOffset);
      const y = verticalPadding + note.string * stringSpacing;
      
      // Get color based on note type
      const colorInfo: ColorInfo = NOTE_COLORS[note.type as keyof typeof NOTE_COLORS] || NOTE_COLORS.OTHER;
      
      // Apply overlay highlighting if this note is an intersection
      let finalColorInfo = colorInfo;
      if (note.overlayType === 'intersection') {
        // Use overlay enhancement colors for intersection notes only when GLOW is enabled
        if (arrangement.overlay?.visualizations?.GLOW) {
          finalColorInfo = {
            bg: OVERLAY_ENHANCEMENTS.intersectionBg,
            border: OVERLAY_ENHANCEMENTS.intersectionBorder,
            text: OVERLAY_ENHANCEMENTS.intersectionText
          };
        }
        // When GLOW is disabled, intersection notes use their normal scale degree colors
      } else if (note.overlayType === 'overlay-only') {
        // Dim notes that are only in overlay scale (not in primary)
        finalColorInfo = {
          bg: colorInfo.bg,
          border: colorInfo.border,
          text: colorInfo.text,
          opacity: 0.3 // Make overlay-only notes semi-transparent
        };
      }

      // Calculate pulse animation - only for overlay intersection notes since CAGED ANIMATED is removed
      let pulseMultiplier = 1.0;
      let pulsedOpacity = 1.0;
      
      // Handle overlay pulse animation for intersection notes
      if (arrangement.overlay?.visualizations?.PULSE && animationStartTimeRef.current && note.overlayType === 'intersection') {
        const currentTime = performance.now();
        const elapsed = (currentTime - animationStartTimeRef.current) / 1000; // Convert to seconds
        
        // Use overlay-specific pulse parameters
        const baseFrequency = OVERLAY_ENHANCEMENTS.pulseFrequency;
        const noteOffset = (note.fret + note.string) * 0.2; // Phase variation
        
        // Create breathing-like pulse for overlay intersection notes
        const rawPulse = Math.sin(elapsed * baseFrequency * 2 * Math.PI + noteOffset);
        const breathingCurve = Math.sin(rawPulse * Math.PI / 2);
        
        // Apply overlay pulse parameters
        const overlayPulseMultiplier = 1.0 + breathingCurve * OVERLAY_ENHANCEMENTS.pulseSizeVariation;
        const overlayPulseOpacity = 0.7 + (breathingCurve + 1) * OVERLAY_ENHANCEMENTS.pulseOpacityVariation * 0.5;
        
        // Set pulse effects
        pulseMultiplier = overlayPulseMultiplier;
        pulsedOpacity = Math.min(1.0, Math.max(0.4, overlayPulseOpacity));
      }
      
      // Calculate note size and appearance (simplified since CAGED GROW is removed)
      // Apply overlay GROW effect - shrink non-intersection notes to emphasize intersection notes
      let overlayGrowMultiplier = 1.0;
      let overlayOpacityMultiplier = 1.0;
      
      if (arrangement.overlay?.visualizations?.GROW) {
        if (note.overlayType === 'intersection') {
          // Keep intersection notes at normal size
          overlayGrowMultiplier = OVERLAY_ENHANCEMENTS.growFillRadiusMultiplier;
        } else {
          // Shrink other notes to emphasize intersection notes
          overlayGrowMultiplier = OVERLAY_ENHANCEMENTS.shrinkFillRadiusMultiplier;
          overlayOpacityMultiplier = OVERLAY_ENHANCEMENTS.shrinkOpacity;
        }
      }
      
      // Simplified size calculation since CAGED GROW is removed - always use normal sizing
      const noteFillRadius = noteRadius * CAGED_ENHANCEMENTS.normalFillRadiusMultiplier * pulseMultiplier * overlayGrowMultiplier;
      const noteBorderRadius = noteRadius * CAGED_ENHANCEMENTS.normalBorderRadiusMultiplier * pulseMultiplier * (overlayGrowMultiplier > 1.0 ? OVERLAY_ENHANCEMENTS.growBorderRadiusMultiplier : (overlayGrowMultiplier < 1.0 ? OVERLAY_ENHANCEMENTS.shrinkBorderRadiusMultiplier : 1.0));
      let noteOpacity = CAGED_ENHANCEMENTS.normalOpacity * pulsedOpacity * overlayOpacityMultiplier;
      const borderWidth = overlayGrowMultiplier > 1.0 ? OVERLAY_ENHANCEMENTS.growBorderWidth : (overlayGrowMultiplier < 1.0 ? CAGED_ENHANCEMENTS.normalBorderWidth : CAGED_ENHANCEMENTS.normalBorderWidth);
      
      // Apply overlay opacity if specified
      if (finalColorInfo.opacity) {
        noteOpacity *= finalColorInfo.opacity;
      }
      
      // Apply overlay-specific visual effects for intersection notes
      if (note.overlayType === 'intersection') {
        if (arrangement.overlay?.visualizations?.GLOW) {
          // GLOW effect - use default golden highlighting (no actual glow, just golden colors)
          // This effect is now applied through the color selection below, no additional rendering needed
        }
        // Note: GROW and PULSE effects are handled in the size calculations above
      }
      
      // Draw note inner circle with enhanced visibility for CAGED shapes
      ctx.globalAlpha = noteOpacity;
      ctx.fillStyle = finalColorInfo.bg;
      ctx.beginPath();
      ctx.arc(x, y, noteFillRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;
      
      // Apply enhanced border for CAGED shapes
      ctx.strokeStyle = finalColorInfo.border;
      ctx.lineWidth = borderWidth;
      ctx.beginPath();
      ctx.arc(x, y, noteBorderRadius, 0, Math.PI * 2);
      ctx.stroke();
      
      // Use pre-calculated optimal text and stroke colors
      const fontFamily = '"Rubik", "Poppins", sans-serif';
      const increment = arrangement.root.showScaleDegrees ? 1 : 1;
      
      // Display the degree or note name
      const displayText = arrangement.root.showScaleDegrees ? 
        (SCALE_DEGREE_DISPLAY_MAP[note.type as ScaleDegree] || note.type) : 
        note.note;
      
      // Use white text for all notes on the fretboard
      const textColor = "white";
      ctx.fillStyle = textColor;
      
      // Check if text contains flat symbol for special rendering
      if (displayText.includes('♭')) {
        // Split the text into main part and flat symbol
        const mainText = displayText.replace('♭', '');
        
        // Font sizes and positioning constants
        const mainFontSize = noteRadius * increment;
        const flatFontSize = mainFontSize * 0.7; // 70% of main size
        const flatDx = -noteRadius * 0.6; // Position flat to the left
        
        // Measure main text and calculate centered position
        ctx.font = `bold ${mainFontSize}px ${fontFamily}`;
        const mainWidth = ctx.measureText(mainText).width;
        const startX = x - (mainWidth / 2) + (noteRadius * 0.7) / 3; // Center with flat offset
        
        ctx.fillStyle = "white";
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        
        // Draw flat symbol with stroke for boldness
        ctx.font = `bold ${flatFontSize}px ${fontFamily}`;
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1;
        ctx.strokeText('♭', startX + flatDx, y);
        ctx.fillText('♭', startX + flatDx, y);
        
        // Draw main text
        ctx.font = `bold ${mainFontSize}px ${fontFamily}`;
        ctx.fillText(mainText, startX, y);
      } else {
        // Normal rendering for text without flat symbol
        ctx.font = `bold ${noteRadius * increment}px ${fontFamily}`;
        ctx.fillStyle = "white";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(displayText, x, y);
      }
      
      // Reset shadow
      ctx.shadowBlur = 0;
    }
    
  }, [
    arrangement,
    dimensions, 
    notes, 
    cagedNotes
  ]);

  // Memoize canvas click handler for performance
  const handleCanvasClick = useCallback((event: MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const xCss = event.clientX - rect.left;
    const yCss = event.clientY - rect.top;

    // Calculate layout parameters for hit testing
    const cssWidth = rect.width;
    const cssHeight = rect.height;
    const verticalPadding = 20;
    const stringSpacing = (cssHeight - verticalPadding * 2) / (NUM_STRINGS - 1);
    const fretSpacing = cssWidth / (NUM_FRETS_DISPLAY + 1);
    const nutWidth = 8;
    const nutOffset = fretSpacing / 2 - nutWidth / 2;
    const noteRadius = Math.min(fretSpacing, stringSpacing) * 0.4;

    // Try to hit a drawn note circle first
    for (const note of notes) {
      const noteX = note.fret === 0
        ? fretSpacing * 0.25
        : (note.fret - 0.5) * fretSpacing + nutOffset;
      const noteY = verticalPadding + note.string * stringSpacing;
      
      // Simplified hit radius since CAGED GROW is removed - always use normal radius
      const hitRadius = noteRadius * CAGED_ENHANCEMENTS.normalBorderRadiusMultiplier;
      
      const dx = xCss - noteX;
      const dy = yCss - noteY;
      if (dx * dx + dy * dy <= hitRadius * hitRadius) {
        // Hit a note: play its frequency
        try {
          setSoundType(soundType);
          const frequency = getFrequencyForPosition(note.string, note.fret);
          playNote(frequency, 800);
        } catch {
          // Failed to play note
        }
        return; // Only play first matched note
      }
    }

    // Click outside note circles: do nothing
  }, [notes, soundType]);

  return (
    <div className="fretboard-container">
      <canvas 
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          cursor: 'pointer'
        }}
        onClick={handleCanvasClick}
      />
    </div>
  );
}

// Memoize Fretboard component for performance optimization
// Re-renders only when arrangement or soundType props change
export default memo(Fretboard, (prevProps, nextProps) => {
  // Custom comparison for deep object equality on arrangement
  const arrangementEqual = 
    prevProps.arrangement.id === nextProps.arrangement.id &&
    JSON.stringify(prevProps.arrangement) === JSON.stringify(nextProps.arrangement);
  
  const soundTypeEqual = prevProps.soundType === nextProps.soundType;
  
  return arrangementEqual && soundTypeEqual;
});
